/**
 * Projects backend order type / channel labels for display.
 * Never invents fulfillment mode — empty when the API omits a usable label.
 */

function isRecord(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === "object" && !Array.isArray(v)
}

function str(v: unknown): string {
  if (v == null) return ""
  return String(v).trim()
}

function humanizeToken(raw: string): string {
  const s = raw.replace(/[_-]+/g, " ").trim()
  if (!s) return ""
  return s
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ")
}

/**
 * Prefer explicit name/label from API objects or strings.
 * Numeric codes are only labeled when the API also supplies a name on a lookup object.
 */
export function resolveOrderFulfillmentLabel(raw: unknown): string {
  if (typeof raw === "string") {
    const t = raw.trim()
    if (!t) return ""
    if (/^\d+$/.test(t)) return ""
    return humanizeToken(t)
  }
  if (typeof raw === "number") return ""
  if (isRecord(raw)) {
    const named = str(raw.name ?? raw.label ?? raw.display_name ?? raw.displayName)
    if (named) return humanizeToken(named)
    const code = str(raw.code)
    if (code && !/^\d+$/.test(code)) return humanizeToken(code)
  }
  return ""
}

/** Channel label e.g. "Website" — same rules as fulfillment. */
export function resolveOrderChannelLabel(raw: unknown): string {
  const label = resolveOrderFulfillmentLabel(raw)
  if (!label) return ""
  // Soft prefix for clarity when API returns a bare channel name.
  if (/^website$/i.test(label)) return "Placed via Website"
  if (/^app$/i.test(label) || /^mobile$/i.test(label)) return "Placed via App"
  return label.startsWith("Placed via") ? label : label
}
